import { Alert, Button, Select, Stack, Text } from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaptureSheet } from "../../capture";
import { CustomerPicker } from "../../components/CustomerPicker";
import { ProductPicker, type OrderLine } from "../../components/ProductPicker";
import { SectionCard } from "../../components/ui";
import { api, type Customer, type PreOrderType } from "../../lib/api";
import { Page } from "../../shell/Page";

const TYPES: { value: PreOrderType; label: string; hint: string }[] = [
  { value: "REGULAR_SALE", label: "Regular sale", hint: "Normal chargeable order" },
  { value: "FOC", label: "FOC", hint: "Free of charge — bills zero, still issues stock" },
  { value: "PRORATA", label: "Prorata", hint: "Part-paid settlement" },
];

/**
 * REQ-MKT-1/2: place a day order with proof of the site visit.
 *
 * Note what this screen does NOT do: check stock. A day order is
 * inventory-neutral by design (REQ-INV-2) — the Billing Executive is the
 * first person to look at availability. Showing stock here would invite
 * field staff to pre-filter demand, which is exactly the signal the business
 * wants to see.
 */
export function PreOrderPlace() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer>();
  const [type, setType] = useState<PreOrderType>("REGULAR_SALE");
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [placed, setPlaced] = useState<{ reference: string }>();

  if (placed) {
    return (
      <Page title="Day order placed" subtitle={placed.reference}>
        <Alert
          variant="light"
          color="teal"
          radius="md"
          icon={<IconCircleCheck size={20} stroke={1.8} />}
          title={`Reference ${placed.reference}`}
        >
          It is now in the Billing Executive&apos;s queue. Stock is checked and
          the order confirmed at billing, not here.
        </Alert>
        <Button
          size="md"
          onClick={() => {
            setPlaced(undefined);
            setCustomer(undefined);
            setLines([]);
            setType("REGULAR_SALE");
          }}
        >
          Place another
        </Button>
        <Button variant="default" size="md" onClick={() => navigate("/marketing")}>
          Back to Marketing
        </Button>
      </Page>
    );
  }

  return (
    <Page
      title="Place a day order"
      subtitle="Stream A · inventory-neutral"
      back="/marketing"
    >
      <CaptureSheet
        kind="PRE_ORDER"
        title="Site visit"
        description="Photo and location prove the visit. Both are required."
        submitLabel="Place day order"
        disabled={!customer || lines.length === 0}
        onSubmit={async (capture) => {
          const { preOrder } = await api.createPreOrder({
            customerId: customer!.id,
            type,
            lineItems: lines.map((line) => ({
              productId: line.productId,
              quantityRequested: line.quantity,
            })),
            capture: {
              ...(capture.gps ? { gps: capture.gps } : {}),
              ...(capture.photoDataUrl ? { photoDataUrl: capture.photoDataUrl } : {}),
              ...(capture.notes ? { notes: capture.notes } : {}),
              clientCapturedAt: capture.clientCapturedAt,
            },
          });
          setPlaced({ reference: preOrder.reference });
        }}
        onCancel={() => navigate("/marketing")}
      >
        <Stack gap="md">
          <SectionCard title="Customer" required>
            <CustomerPicker value={customer} onChange={setCustomer} />
          </SectionCard>

          <SectionCard title="What they want" required>
            <Stack gap="md">
              {/* A real label via the control's own prop rather than a
                  duplicated aria-label: two copies of the same name are two
                  things to keep in step, and only one of them is what a
                  sighted person reads. */}
              <Select
                id="pre-order-type"
                label="Order type"
                value={type}
                onChange={(value) => setType((value as PreOrderType) ?? "REGULAR_SALE")}
                allowDeselect={false}
                data={TYPES.map((option) => ({
                  value: option.value,
                  label: `${option.label} — ${option.hint}`,
                }))}
              />
              <ProductPicker lines={lines} onChange={setLines} />
              {lines.length === 0 && (
                <Text fz="sm" c="gray.6">
                  Add at least one product before placing the day order.
                </Text>
              )}
            </Stack>
          </SectionCard>
        </Stack>
      </CaptureSheet>
    </Page>
  );
}
